
import './bootstrap';

// BRO-749: Initialize WebSocket connection manager for tab visibility,
// health checks, and reconnection handling. Was previously orphaned
// in bootstrap.react.js — never imported, never running.
import wsConnectionManager from './services/WebSocketConnectionManager';
window.wsConnectionManager = wsConnectionManager;
wsConnectionManager.init();

import Alpine from 'alpinejs';
import intersect from '@alpinejs/intersect'
import collapse from '@alpinejs/collapse'
// BRO-1498: x-trap comes from this plugin. The marketing mobile menu has asked for
// it at components/marketing/header.blade.php since the menu was written, and Alpine
// ignores a directive it has no plugin for SILENTLY, so the markup looked correct and
// a keyboard user tabbed straight out of the open menu into the page behind it.
import focus from '@alpinejs/focus'
import Sortable from 'sortablejs';

// Register Alpine plugins
Alpine.plugin(collapse)
// BRO-1498: gives x-trap. It also provides focus-on-open and focus-return-on-close, so
// none of that is hand-rolled: the directive already does all three, and writing them
// alongside would be a second implementation of the same behaviour.
Alpine.plugin(focus)

// Make Sortable available globally for Alpine components
window.Sortable = Sortable;

// Make Alpine available globally before importing components that use it
window.Alpine = Alpine;

// Core components used across multiple pages
import './phone-input';

// Import the notification service initialization
import './notification-init';

// BRO-1567: lets the service worker ask any open window whether privacy mode is on before it
// shows a push notification carrying a contact's name and message text. Imported here rather
// than from PushNotificationService so that ANY open page can answer, not only one that has
// finished setting push up.
import { registerPrivacyServiceWorkerBridge } from './utils/privacyServiceWorkerBridge';

registerPrivacyServiceWorkerBridge();

// Import KB search for documentation pages
import './kb-search';

// BRO-458: Session timeout warning
import './session-timeout';

// BRO-388/BRO-944: dashboard realtime is per-widget (each subscribes to Echo directly,
// like LiveFeedBadge and NotificationBell). The old centralized dashboard-realtime.js was
// orphaned by BRO-388 and deleted in BRO-944 (it was dead but still being maintained).

// Register dashboard grid component directly here to ensure it's available
Alpine.data('dashboardGrid', () => ({
    sortable: null,
    widgets: [],
    originalOrder: [],
    isSaving: false,
    hasChanges: false,
    
    init() {
        // Get initial widget order from DOM or saved layout
        this.initializeWidgets();
        
        // Initialize Sortable.js
        this.$nextTick(() => {
            const gridElement = this.$refs.dashboardGrid;
            if (gridElement && window.Sortable) {
                this.sortable = new Sortable(gridElement, {
                    animation: 150,
                    ghostClass: 'opacity-50',
                    dragClass: 'dragging',
                    handle: '.widget-handle', // Drag handle
                    onEnd: (evt) => {
                        this.handleDragEnd(evt);
                    },
                });
            }
        });
    },
    
    initializeWidgets() {
        // Get widgets from DOM or saved layout
        const savedLayout = this.$el.dataset.layout ? JSON.parse(this.$el.dataset.layout) : null;
        const widgetElements = this.$refs.dashboardGrid?.querySelectorAll('.dashboard-widget') || [];
        
        if (savedLayout && savedLayout.order) {
            // Use saved layout order
            this.widgets = savedLayout.order;
            this.reorderDOM(savedLayout.order);
        } else {
            // Use DOM order
            this.widgets = Array.from(widgetElements).map(el => el.dataset.widgetId);
        }
        
        this.originalOrder = [...this.widgets];
    },
    
    reorderDOM(order) {
        const gridElement = this.$refs.dashboardGrid;
        if (!gridElement) return;
        
        // Create a document fragment to minimize DOM operations
        const fragment = document.createDocumentFragment();
        
        order.forEach(widgetId => {
            const widget = gridElement.querySelector(`[data-widget-id="${widgetId}"]`);
            if (widget) {
                fragment.appendChild(widget);
            }
        });
        
        // Append any widgets not in the saved order (new widgets)
        const allWidgets = gridElement.querySelectorAll('.dashboard-widget');
        allWidgets.forEach(widget => {
            if (!order.includes(widget.dataset.widgetId)) {
                fragment.appendChild(widget);
            }
        });
        
        gridElement.appendChild(fragment);
    },
    
    handleDragEnd(evt) {
        // Update widget order after drag
        const newOrder = Array.from(this.$refs.dashboardGrid.querySelectorAll('.dashboard-widget'))
            .map(el => el.dataset.widgetId);
        
        this.widgets = newOrder;
        this.hasChanges = JSON.stringify(this.widgets) !== JSON.stringify(this.originalOrder);
        
        // Auto-save after a delay
        if (this.hasChanges) {
            this.debouncedSave();
        }
    },
    
    debouncedSave: Alpine.debounce(function() {
        this.saveLayout();
    }, 1000),
    
    async saveLayout() {
        if (!this.hasChanges || this.isSaving) return;
        
        this.isSaving = true;
        
        try {
            const response = await fetch('/api/dashboard/layout', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content,
                    'Accept': 'application/json',
                },
                body: JSON.stringify({
                    layout: {
                        order: this.widgets,
                        timestamp: new Date().toISOString()
                    }
                }),
            });
            
            if (response.ok) {
                this.originalOrder = [...this.widgets];
                this.hasChanges = false;
                this.showNotification('Layout saved', 'success');
            } else {
                throw new Error('Failed to save layout');
            }
        } catch (error) {
            console.error('Error saving layout:', error);
            this.showNotification('Failed to save layout', 'error');
        } finally {
            this.isSaving = false;
        }
    },
    
    async resetLayout() {
        if (!confirm('Reset dashboard to default layout?')) return;
        
        this.isSaving = true;
        
        try {
            const response = await fetch('/api/dashboard/layout', {
                method: 'DELETE',
                headers: {
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content,
                    'Accept': 'application/json',
                },
            });
            
            if (response.ok) {
                // Reload page to get default layout
                window.location.reload();
            } else {
                throw new Error('Failed to reset layout');
            }
        } catch (error) {
            console.error('Error resetting layout:', error);
            this.showNotification('Failed to reset layout', 'error');
        } finally {
            this.isSaving = false;
        }
    },
    
    showNotification(message, type = 'info') {
        // Create a simple notification
        const notification = document.createElement('div');
        notification.className = `fixed bottom-4 right-4 px-4 py-2 rounded-lg text-white z-50 ${
            type === 'success' ? 'bg-green-600' : 
            type === 'error' ? 'bg-red-600' : 
            'bg-blue-600'
        }`;
        notification.textContent = message;
        document.body.appendChild(notification);
        
        setTimeout(() => {
            notification.remove();
        }, 3000);
    }
}));

Alpine.plugin(intersect)

Alpine.start();
